import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  RotateCcw,
  HandHeart,
  Heart,
  ListChecks,
  PackageCheck,
  Play,
} from "lucide-react";import useActivity from "@/hooks/useActivity";
import useSeo from "@/hooks/useSeo";
import { useApp } from "@/context/AppContext";
import confetti from "canvas-confetti";
import { setVolunteerProgress, resetVolunteer, getKitsPacked, incrementKitsPacked, resetKitsPacked } from "@/lib/storage";
import ProgressBar from "@/components/activity/ProgressBar";
import Callout from "@/components/activity/Callout";
import { LoadingState, ErrorState, NotFoundState } from "@/components/common/States";

// Bold ONLY the quantity tied to the step's "Pick"/"Pick up" instruction,
// never arbitrary digits that appear inside an item name or elsewhere.
function renderWithQty(text) {
  const str = String(text);
  const m = str.match(/\bPick(?:\s+up)?\s+(\d+)/i);
  if (!m) return str;
  const num = m[1];
  const start = m.index + m[0].length - num.length;
  return [
    str.slice(0, start),
    <strong key="qty" className="font-bold text-brand-orange">{num}</strong>,
    str.slice(start + num.length),
  ];
}

export default function VolunteerFlow() {  const { slug } = useParams();
  const { activity, status } = useActivity(slug);
  const { role, setRole } = useApp();
  useSeo("Volunteer Guide", "Follow the guided steps to pack and personalize a complete kit \u2014 no briefing needed.");

  useEffect(() => {
    if (role !== "volunteer") setRole("volunteer");
  }, [role, setRole]);

  // The step walkthrough always starts fresh on each entry into the guide, so
  // no step is ever pre-marked "done" from a previous visit. (The all-time
  // "kits packed" counter below is tracked separately and does persist.)
  const [progress, setProgress] = useState({ stepIndex: 0, done: {}, gathered: {}, quality: {}, kitsDone: 0, phase: "packing" });
  const [kitsPacked, setKitsPacked] = useState(() => getKitsPacked(slug));

  // Persist within the session (used for reset/clearing); intentionally not
  // read back on mount so re-entering the guide is always a clean start.
  useEffect(() => {
    setVolunteerProgress(slug, progress);
  }, [slug, progress]);

  // Celebratory confetti burst when the final "You Did It!" screen appears
  // (repeat-kit loops only - only they set phase to "final"). Short, one-shot.
  useEffect(() => {
    if (progress.phase !== "final") return;
    const colors = ["#F97316", "#0B2545", "#FDE68A", "#ffffff"];
    const end = Date.now() + 1200;
    let raf;
    const frame = () => {
      confetti({ particleCount: 4, angle: 60, spread: 60, startVelocity: 45, origin: { x: 0, y: 0.9 }, colors, disableForReducedMotion: true });
      confetti({ particleCount: 4, angle: 120, spread: 60, startVelocity: 45, origin: { x: 1, y: 0.9 }, colors, disableForReducedMotion: true });
      if (Date.now() < end) raf = requestAnimationFrame(frame);
    };
    confetti({ particleCount: 80, spread: 80, startVelocity: 40, origin: { x: 0.5, y: 0.35 }, colors, disableForReducedMotion: true });
    frame();
    return () => raf && cancelAnimationFrame(raf);
  }, [progress.phase]);

  const flow = activity?.volunteerFlow;

  const stages = useMemo(() => {
    if (!flow) return [];
    const list = [{ type: "welcome" }, { type: "why" }];
    // Some activities (e.g. supply-drive kits built progressively) have no
    // single "gather" step - only include it when there are materials to list.
    if (flow.gatherMaterials && flow.gatherMaterials.length > 0) {
      list.push({ type: "gather" });
    }
    flow.steps.forEach((s) => list.push({ type: "step", step: s }));
    list.push({ type: "quality" });
    // Repeat-kit activities handle completion via the kit-counter flow, so they
    // don't use the single linear "done" stage.
    if (!flow.repeatKit) list.push({ type: "done" });
    return list;
  }, [flow]);

  const index = Math.min(progress.stepIndex || 0, Math.max(0, stages.length - 1));

  const goTo = useCallback((i) => {
    setProgress((p) => ({ ...p, stepIndex: Math.max(0, i) }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const toggleGather = useCallback((i) => {
    setProgress((p) => {
      const gathered = { ...(p.gathered || {}) };
      if (gathered[i]) delete gathered[i];
      else gathered[i] = true;
      return { ...p, gathered };
    });
  }, []);

  const toggleQuality = useCallback((i) => {
    setProgress((p) => {
      const quality = { ...(p.quality || {}) };
      if (quality[i]) delete quality[i];
      else quality[i] = true;
      return { ...p, quality };
    });
  }, []);

  const markStepDone = useCallback((stepNumber, nextIndex) => {
    setProgress((p) => {
      const done = { ...(p.done || {}), [stepNumber]: true };
      return { ...p, done, stepIndex: nextIndex };
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  function handleReset() {
    if (window.confirm("Start over from the beginning? Your ticked items will be cleared.")) {
      resetVolunteer(slug);
      resetKitsPacked(slug);
      setKitsPacked(0);
      setProgress({ stepIndex: 0, done: {}, gathered: {}, quality: {}, kitsDone: 0, phase: "packing" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  if (status === "loading") return <LoadingState />;
  if (status === "notfound") return <NotFoundState />;
  if (status === "error" || !activity || !flow) return <ErrorState onRetry={() => window.location.reload()} />;

  const stage = stages[index];
  const pct = (index / (stages.length - 1)) * 100;
  const isLast = index === stages.length - 1;

  // ---- Repeat-kit flow (4 kits per volunteer) ----------------------------
  const repeat = flow.repeatKit;
  const isRepeat = !!repeat;
  const kitsPerVolunteer = repeat?.kitsPerVolunteer || 4;
  const kitsDone = progress.kitsDone || 0;
  const phase = isRepeat ? progress.phase || "packing" : "packing";

  function completeKit() {
    setProgress((p) => {
      const next = (p.kitsDone || 0) + 1;
      return { ...p, kitsDone: next, phase: next >= kitsPerVolunteer ? "final" : "kitComplete" };
    });
    setKitsPacked(incrementKitsPacked(slug));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function packAnother() {
    const startIdx = stages.findIndex((s) => s.type === "step");
    setProgress((p) => ({
      ...p,
      stepIndex: startIdx < 0 ? 0 : startIdx,
      done: {},
      gathered: {},
      quality: {},
      phase: "packing",
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function skipToFinal() {
    setProgress((p) => ({ ...p, kitsDone: kitsPerVolunteer, phase: "final" }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const showNav = isRepeat ? phase === "packing" : !isLast;

  let headerStatus;
  if (phase === "final") headerStatus = "Complete";
  else if (phase === "kitComplete") headerStatus = `Kit ${kitsDone} of ${kitsPerVolunteer}`;
  else if (!isRepeat && isLast) headerStatus = "Complete";
  else if (stage?.type === "step") headerStatus = `Step ${stage.step.number} of ${flow.steps.length}`;
  else if (stage?.type === "welcome") headerStatus = "Get started";
  else if (stage?.type === "why") headerStatus = "Why it matters";
  else if (stage?.type === "gather") headerStatus = "Gather materials";
  else if (stage?.type === "quality") headerStatus = "Quality check";
  else headerStatus = `Step ${index + 1} of ${stages.length}`;

  let barValue;
  if (phase === "final") barValue = 100;
  else if (phase === "kitComplete") barValue = (kitsDone / kitsPerVolunteer) * 100;
  else if (!isRepeat && isLast) barValue = 100;
  else barValue = pct;

  return (
    <div className="min-h-[70vh]">
      {/* Sticky progress header */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-brand-navy/10">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3 mb-2">
            <Link
              to={`/activities/${slug}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand-navy/60 hover:text-brand-orange"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> {activity.name}
            </Link>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {kitsPacked > 0 && (
                <span
                  data-testid="kits-packed-badge"
                  className="inline-flex items-center gap-1 rounded-full bg-brand-orange/12 text-brand-orange px-2.5 py-1 text-xs font-bold"
                >
                  <PackageCheck className="w-3.5 h-3.5" />
                  {kitsPacked} {kitsPacked === 1 ? "kit" : "kits"} packed
                </span>
              )}
              <span className="text-xs font-semibold text-brand-navy/60">
                {headerStatus}
              </span>
              <button
                data-testid="vol-reset"
                onClick={handleReset}
                className="inline-flex items-center gap-1 text-xs font-semibold text-brand-navy/60 hover:text-brand-orange"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Reset progress
              </button>
            </div>
          </div>
          <ProgressBar value={barValue} />
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        <div key={phase === "packing" ? `s${index}` : phase} className="animate-fade-up">
          {phase === "packing" && (
          <>
          {stage.type === "welcome" && (
            <StageCard icon={HandHeart} eyebrow="Volunteer Guide" title={flow.welcome.title}>
              <p className="text-lg text-brand-navy/80 leading-relaxed">{flow.welcome.body}</p>
            </StageCard>
          )}

          {stage.type === "why" && (
            <StageCard icon={Heart} eyebrow="A quick why" title={flow.why.title}>
              <p className="text-lg text-brand-navy/80 leading-relaxed">{flow.why.body}</p>
            </StageCard>
          )}

          {stage.type === "gather" && (
            <StageCard icon={PackageCheck} eyebrow="Get ready" title={flow.gatherTitle || "Gather your materials"}>
              <p className="text-brand-navy/70 mb-4">{flow.gatherIntro || "Everything is on the table. Tick each as you collect it."}</p>
              <ul className="space-y-2.5" data-testid="gather-list">
                {flow.gatherMaterials.map((m, i) => {
                  const checked = !!progress.gathered?.[i];
                  return (
                    <li key={i}>
                      <button
                        data-testid={`gather-item-${i}`}
                        onClick={() => toggleGather(i)}
                        className={`w-full flex items-center gap-3 rounded-2xl border p-3.5 min-h-[44px] text-left transition-colors ${
                          checked ? "bg-brand-cream border-brand-orange/40" : "bg-white border-brand-navy/10 hover:border-brand-orange/40"
                        }`}
                      >
                        <span className={`flex-shrink-0 w-7 h-7 rounded-lg border-2 flex items-center justify-center ${checked ? "bg-brand-orange border-brand-orange" : "border-brand-navy/25"}`}>
                          {checked && <Check className="w-5 h-5 text-white" strokeWidth={3} />}
                        </span>
                        <span className={`flex-1 text-[15px] ${checked ? "text-brand-navy/60 line-through" : "text-brand-navy"}`}>
                          {m.instruction}
                        </span>
                        <span className="flex-shrink-0 font-display text-lg text-brand-orange">{m.qty}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </StageCard>
          )}

          {stage.type === "step" && (
            <StageCard
              icon={null}
              eyebrow={`Step ${stage.step.number} of ${flow.steps.length}`}
              title={stage.step.title}
              bigNumber={stage.step.number}
            >
              {/* Illustration placeholder */}
              <div className="rounded-2xl bg-brand-cream h-40 sm:h-48 flex items-center justify-center mb-4">
                {stage.step.iconImg ? (
                  <img src={stage.step.iconImg} alt="" aria-hidden="true" className="h-28 sm:h-32 w-auto object-contain rounded-xl" />
                ) : (
                  <span className="text-6xl" aria-hidden="true">{stage.step.emoji || ["\u270D\uFE0F", "\u2764\uFE0F", "\u270F\uFE0F", "\u2B50", "\uD83D\uDCE6", "\uD83C\uDF80"][stage.step.number - 1] || "\u2728"}</span>
                )}
              </div>
              {stage.step.secondaryImg && (
                <div className="mb-4 rounded-2xl bg-brand-cream flex flex-col items-center justify-center p-4" data-testid="step-secondary-image">
                  {stage.step.secondaryLabel && (
                    <p className="text-xs font-bold uppercase tracking-wide text-brand-orange mb-2">{stage.step.secondaryLabel}</p>
                  )}
                  <img src={stage.step.secondaryImg} alt="" aria-hidden="true" className="h-32 sm:h-40 w-auto object-contain rounded-xl" />
                </div>
              )}
              <p className="text-lg text-brand-navy/85 leading-relaxed whitespace-pre-line">{renderWithQty(stage.step.body)}</p>
              {stage.step.videoUrl && (
                <a
                  data-testid="step-video-link"
                  href={stage.step.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-orange/12 hover:bg-brand-orange/20 text-brand-orange font-semibold text-sm min-h-[44px] px-5 transition-colors"
                >
                  <Play className="w-4 h-4 fill-current" /> Tap here for video instructions
                </a>
              )}
              {stage.step.tip && <div className="mt-4"><Callout>{stage.step.tip}</Callout></div>}
            </StageCard>
          )}

          {stage.type === "quality" && (
            <StageCard icon={ListChecks} eyebrow="Almost there" title="Final quality check">
              <p className="text-brand-navy/70 mb-4">{flow.qualityIntro || "Check the bag has everything before you drop it off."}</p>
              <ul className="space-y-2.5" data-testid="quality-list">
                {flow.qualityChecklist.map((q, i) => {
                  const checked = !!progress.quality?.[i];
                  return (
                    <li key={i}>
                      <button
                        data-testid={`quality-item-${i}`}
                        onClick={() => toggleQuality(i)}
                        className={`w-full flex items-center gap-3 rounded-2xl border p-3.5 min-h-[44px] text-left transition-colors ${
                          checked ? "bg-brand-cream border-brand-orange/40" : "bg-white border-brand-navy/10 hover:border-brand-orange/40"
                        }`}
                      >
                        <span className={`flex-shrink-0 w-7 h-7 rounded-lg border-2 flex items-center justify-center ${checked ? "bg-brand-orange border-brand-orange" : "border-brand-navy/25"}`}>
                          {checked && <Check className="w-5 h-5 text-white" strokeWidth={3} />}
                        </span>
                        <span className={`flex-1 text-[15px] ${checked ? "text-brand-navy/60 line-through" : "text-brand-navy"}`}>{q}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {flow.qualityNote && (
                <div className="mt-4">
                  <Callout title="Then pack up">{flow.qualityNote}</Callout>
                </div>
              )}
            </StageCard>
          )}

          {stage.type === "done" && (
            <div className="text-center rounded-3xl bg-brand-navy text-white p-8 sm:p-12 relative overflow-hidden animate-pop" data-testid="done-screen">
              <div className="text-7xl mb-4 animate-pop" aria-hidden="true">{"\uD83C\uDF89"}</div>
              <h2 className="display-title text-3xl sm:text-4xl">{flow.done.title}</h2>
              <p className="mt-3 max-w-md mx-auto text-white/90 text-lg">{flow.done.body}</p>
              <button
                data-testid="volunteer-restart"
                onClick={handleReset}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold min-h-[48px] px-6 transition-colors"
              >
                <RotateCcw className="w-4 h-4" /> Start another kit
              </button>
            </div>
          )}
          </>
          )}

          {isRepeat && phase === "kitComplete" && (
            <div className="text-center rounded-3xl bg-brand-navy text-white p-8 sm:p-12 relative overflow-hidden animate-pop" data-testid="kit-complete-screen">
              <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-brand-orange flex items-center justify-center animate-pop">
                <PackageCheck className="w-9 h-9 text-white" />
              </div>
              <p className="text-xs font-bold uppercase tracking-wide text-brand-orange">Nice work</p>
              <h2 className="display-title text-3xl sm:text-4xl mt-1" data-testid="kit-complete-heading">
                Kit {kitsDone} of {kitsPerVolunteer} done!
              </h2>
              <p className="mt-3 max-w-md mx-auto text-white/90 text-lg" data-testid="kit-complete-message">
                {repeat.messages[kitsDone - 1]}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  data-testid="pack-another-kit"
                  onClick={packAnother}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold min-h-[48px] px-6 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" /> Pack Another Kit
                </button>
                <button
                  data-testid="packed-all-kits"
                  onClick={skipToFinal}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold min-h-[48px] px-6 transition-colors"
                >
                  <Check className="w-4 h-4" /> I've packed all {kitsPerVolunteer} kits
                </button>
              </div>
            </div>
          )}

          {isRepeat && phase === "final" && (
            <div className="text-center rounded-3xl bg-brand-navy text-white p-8 sm:p-12 relative overflow-hidden animate-pop" data-testid="done-screen">
              <div className="text-7xl mb-4 animate-pop" aria-hidden="true">{"\uD83C\uDF89"}</div>
              <h2 className="display-title text-3xl sm:text-4xl">{flow.done.title}</h2>
              <p className="mt-3 max-w-md mx-auto text-white/90 text-lg" data-testid="final-message">
                {repeat.messages[kitsPerVolunteer - 1]}
              </p>
              <button
                data-testid="volunteer-restart"
                onClick={handleReset}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold min-h-[48px] px-6 transition-colors"
              >
                <RotateCcw className="w-4 h-4" /> Start a new session
              </button>
            </div>
          )}
        </div>

        {/* Navigation controls */}
        {showNav && (
          <div className="mt-8 flex items-center justify-between gap-3">
            <button
              data-testid="vol-back"
              onClick={() => goTo(index - 1)}
              disabled={index === 0}
              className="inline-flex items-center gap-2 rounded-full border-2 border-brand-navy/15 text-brand-navy font-semibold min-h-[48px] px-5 disabled:opacity-40 hover:border-brand-orange transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            {stage.type === "step" ? (
              <button
                data-testid="vol-mark-done"
                onClick={() => markStepDone(stage.step.number, index + 1)}
                className="inline-flex items-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold min-h-[48px] px-6 transition-colors"
              >
                <Check className="w-4 h-4" /> Mark done
              </button>
            ) : (
              <button
                data-testid="vol-next"
                onClick={() => {
                  if (stage.type === "quality") {
                    if (isRepeat) completeKit();
                    else {
                      setKitsPacked(incrementKitsPacked(slug));
                      goTo(index + 1);
                    }
                  } else {
                    goTo(index + 1);
                  }
                }}
                className="inline-flex items-center gap-2 rounded-full bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold min-h-[48px] px-6 transition-colors"
              >
                {stage.type === "quality" ? "Finish" : "Continue"} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

function StageCard({ icon: Icon, eyebrow, title, bigNumber, children }) {
  return (
    <div className="rounded-3xl bg-white border border-brand-navy/10 pib-shadow p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-3">
        {bigNumber ? (
          <div className="w-12 h-12 rounded-xl bg-brand-orange text-white font-display text-2xl flex items-center justify-center">
            {bigNumber}
          </div>
        ) : Icon ? (
          <div className="w-12 h-12 rounded-xl bg-brand-orange/12 flex items-center justify-center">
            <Icon className="w-6 h-6 text-brand-orange" />
          </div>
        ) : null}
        <div>
          {eyebrow && <p className="text-xs font-bold uppercase tracking-wide text-brand-orange">{eyebrow}</p>}
          <h2 className="display-title text-2xl sm:text-3xl text-brand-navy leading-tight">{title}</h2>
        </div>
      </div>
      {children}
    </div>
  );
}
